import React from 'react';
import { useTheme } from '@mui/material/styles';
import Link from 'next/link';
import {
  Typography, Grid, Box, FormControl, Button, IconButton,
  Backdrop, InputBase, Dialog, DialogActions, DialogContent,
  DialogTitle, Slide, CircularProgress, Tooltip, styled, Divider, Chip, InputAdornment
} from '@mui/material';
import { useRouter } from 'next/router';
import LoginRoundedIcon from '@mui/icons-material/LoginRounded';
import TwitterIcon from '@mui/icons-material/X';
import Google48 from '../../../public/assets/svg/google48.svg';
import { dataConstants } from '../../constants';
import useAuth from '../../hook/auth';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import MailIcon from '../../static/images/component/mail.svg';
import BorderColorRoundedIcon from '@mui/icons-material/BorderColorRounded';
import { default as LogoWhite } from '../_common/Logo';
import PremiumBanner from '../Pricing/PremiumBanner';
import NorthEastRoundedIcon from '@mui/icons-material/NorthEastRounded';
import { handleFirebaseError } from '../../utils';
import * as send from '../../../lib/analytics/send';
import { ClickAwayListener } from '@mui/base/ClickAwayListener';
import { colors, radii, shadows, typography as dt } from '../../utils/designTokens';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const EmailInput = styled(InputBase)(() => ({
  '& .MuiInputBase-input.Mui-disabled': {
    backgroundColor: colors.neutral[100],
    WebkitTextFillColor: colors.neutral[600],
  },
  '& .MuiInputBase-input': {
    borderRadius: radii.md,
    backgroundColor: colors.neutral[0],
    border: `1.5px solid ${colors.neutral[200]}`,
    fontSize: 15,
    fontFamily: dt.fontFamily,
    fontWeight: 400,
    width: '100%',
    padding: '14px 16px',
    transition: 'border-color 200ms ease, box-shadow 200ms ease',
    '&:hover': {
      borderColor: colors.neutral[400],
    },
    '&:focus': {
      boxShadow: `0 0 0 4px ${colors.accent[100]}`,
      borderColor: colors.accent.main,
    },
    '&::placeholder': {
      color: colors.neutral[400],
      opacity: 1,
    },
  },
}));

const SocialButton = styled(Button)(() => ({
  justifyContent: 'center',
  borderRadius: radii.md,
  fontWeight: 500,
  width: '100%',
  maxWidth: 360,
  padding: '12px 20px',
  backgroundColor: colors.neutral[0],
  border: `1.5px solid ${colors.neutral[200]}`,
  textTransform: 'none',
  color: colors.neutral[800],
  fontSize: '0.938rem',
  letterSpacing: '-0.01em',
  transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    backgroundColor: colors.neutral[50],
    borderColor: colors.neutral[400],
    boxShadow: shadows.md,
    transform: 'translateY(-1px)',
  },
  '&:active': {
    transform: 'translateY(0)',
    boxShadow: shadows.sm,
  },
  '&.Mui-disabled': {
    backgroundColor: colors.neutral[100],
    borderColor: colors.neutral[200],
    color: colors.neutral[400],
  },
  '& .MuiButton-startIcon': {
    marginRight: 12,
  },
}));

export default function Auth(props) {
  const theme = useTheme();
  const auth = useAuth();
  const {
    plans, gotoStripe, monthlyPlan, yearlyPlan, pricePlans,
    maxWidth, updateEmail, tryPasswordlessSignIn, errorResponse, savedEmail,
    checkinboxpop, setCheckinboxpop, isPopup, comp,
  } = props || {};
  const {
    user, loginWithGoogle, loginWithTwitter,
    signInUserWithEmailAndPassword, createUserWithEmailAndPassword,
    signInUserWithEmail, resetPassword, logout, error,
  } = auth || {};
  const { reloadUserInfo } = user || {};
  const isPremium = reloadUserInfo?.customAttributes
    && (JSON.parse(reloadUserInfo.customAttributes)?.userType === 'PREMIUM'
      || JSON.parse(reloadUserInfo.customAttributes)?.userType === 'PROFESSIONAL');

  const router = useRouter();
  const email = React.useRef(
    typeof window !== 'undefined' && window.localStorage.getItem('emailForSignIn') || '',
  );
  const [values, setValues] = React.useState({
    email: typeof window !== 'undefined' && window.localStorage.getItem('emailForSignIn') || '',
    password: '',
  });

  const [usePassword, setUsePassword] = React.useState(true); // Defaulted to true
  const [showPassword, setShowPassword] = React.useState(false);
  const [isSignUp, setIsSignUp] = React.useState(false);
  const { redirect_to } = router.query;
  const [loginLoading, setLoginLoading] = React.useState(false);
  const [tabValue, setTabValue] = React.useState(0);
  const [openBackdrop, setOpenBackdrop] = React.useState(false);

  React.useEffect(() => {
    if ((!isPremium || redirect_to) && user) setTabValue(1);
    else setTabValue(0);
    if (isPremium) setTabValue(0);
    setCheckinboxpop(false);
  }, [user]);

  React.useEffect(() => {
    if (user?.email) {
      setLoginLoading(false);
    }
  }, [user]);

  React.useEffect(() => {
    if (error?.[router?.pathname] || errorResponse) {
      setLoginLoading(false);
    }
  }, [error, errorResponse, router?.pathname]);

  React.useEffect(() => {
    if (checkinboxpop) {
      setLoginLoading(false);
    }
  }, [checkinboxpop]);

  const handleChange = (prop) => (event) => {
    if (prop === 'email' && updateEmail) updateEmail(event);
    setValues({ ...values, [prop]: event.target.value });
  };

  const handlePasswordAuth = async (e) => {
    e.preventDefault();
    setLoginLoading(true);

    if (isSignUp) {
      send.uevent(e, 'signup_with_password');
      await createUserWithEmailAndPassword(values.email, values.password);
    } else {
      send.uevent(e, 'login_with_password');
      await signInUserWithEmailAndPassword(values.email, values.password);
    }
  };

  const renderLoginPanel = () => (
    <Box sx={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', minHeight: isPopup ? 'auto' : '70vh',
      py: { xs: 4, md: 6 },
    }}>
      <Box sx={{
        width: '100%',
        maxWidth: 420,
        minWidth: { xs: '100%', md: 420 },
        mx: 'auto',
        position: 'relative',
        bgcolor: colors.neutral[0],
        borderRadius: `${radii.xl}px`,
        border: `1px solid ${colors.neutral[200]}`,
        boxShadow: isPopup ? 'none' : '0 4px 24px -4px rgba(0,0,0,0.08), 0 2px 8px -2px rgba(0,0,0,0.04)',
        p: { xs: '32px 24px', md: '40px 36px' },
        overflow: 'hidden',
      }}>
        {loginLoading && !(error?.[router?.pathname] || errorResponse) && (
          <ClickAwayListener onClickAway={() => setLoginLoading(false)}>
            <Box sx={{
              display: 'flex', justifyContent: 'center', alignItems: 'center',
              position: 'absolute', inset: 0, zIndex: 100,
              bgcolor: 'rgba(255,255,255,0.75)', backdropFilter: 'blur(4px)',
              borderRadius: `${radii.xl}px`,
            }}>
              <CircularProgress size={32} sx={{ color: colors.accent.main }} />
            </Box>
          </ClickAwayListener>
        )}

        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography sx={{
            fontWeight: 700, color: colors.neutral[900],
            fontSize: { xs: '1.5rem', md: '1.75rem' },
            letterSpacing: '-0.02em', lineHeight: 1.2,
          }}>
            Welcome to Grufity
          </Typography>
          <Typography sx={{
            mt: 1, color: colors.neutral[500], fontSize: '0.938rem', lineHeight: 1.5, display: 'block',
          }}>
            Sign in or create an account to continue
          </Typography>
        </Box>

        {user?.email ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Button
              onClick={auth?.logout}
              size="small"
              startIcon={<LoginRoundedIcon />}
              sx={{
                textTransform: 'none', fontSize: '.875rem', fontWeight: 500,
                color: colors.accent.main, borderColor: colors.accent[200],
                borderRadius: radii.sm, px: 3, py: 1,
                '&:hover': { bgcolor: colors.accent[50], borderColor: colors.accent.main },
              }}
              variant="outlined"
            >
              Login with a different account
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>

            {(error?.[router?.pathname] || errorResponse) && (
              <Box sx={{
                mb: 3, width: '100%', maxWidth: 360, p: '10px 14px', borderRadius: radii.sm,
                bgcolor: colors.error.bg, border: `1px solid ${colors.error.light}20`,
              }}>
                <Typography sx={{ fontSize: '.84rem', color: colors.error.dark, textAlign: 'center' }}>
                  {handleFirebaseError((error?.[router?.pathname] || '').replace(/^Firebase: Error \(([^)]+)\)\.$/, '$1')) || errorResponse}
                </Typography>
              </Box>
            )}

            <Box
              component="form"
              noValidate
              autoComplete="off"
              onSubmit={(e) => e.preventDefault()}
              sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%', alignItems: 'center' }}
            >
              <Box sx={{ width: '100%', maxWidth: 360 }}>
                <Typography sx={{ fontSize: '.84rem', fontWeight: 600, color: colors.neutral[700], mb: 0.8, ml: 0.5 }}>
                  Email address
                </Typography>
                <FormControl error={!!(error?.[router?.pathname] || errorResponse)} sx={{ width: '100%' }} variant="standard">
                  <EmailInput
                    disabled={!!user?.email || loginLoading}
                    defaultValue={user?.email || savedEmail}
                    type="email"
                    placeholder="you@example.com"
                    value={values.email}
                    ref={email}
                    focused
                    onChange={handleChange('email')}
                  />
                </FormControl>
              </Box>

              {usePassword && (
                <Box sx={{ width: '100%', maxWidth: 360, animation: 'fadeIn 300ms ease-in-out' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.8, ml: 0.5 }}>
                    <Typography sx={{ fontSize: '.84rem', fontWeight: 600, color: colors.neutral[700] }}>
                      {isSignUp ? "Create a Password" : "Password"}
                    </Typography>
                  </Box>
                  <FormControl error={!!(error?.[router?.pathname] || errorResponse)} sx={{ width: '100%' }} variant="standard">
                    <EmailInput
                      disabled={loginLoading}
                      type={showPassword ? 'text' : 'password'}
                      placeholder={isSignUp ? "Minimum 6 characters" : "Enter your password"}
                      value={values.password}
                      onChange={handleChange('password')}
                      endAdornment={
                        <InputAdornment position="end" sx={{ mr: 1 }}>
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => setShowPassword((show) => !show)}
                            edge="end"
                            size="small"
                            sx={{ color: colors.neutral[400] }}
                          >
                            {showPassword ? <VisibilityOffRoundedIcon fontSize="small" /> : <VisibilityRoundedIcon fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      }
                    />
                  </FormControl>
                </Box>
              )}

              <Box sx={{ width: '100%', maxWidth: 360 }}>
                <Typography sx={{
                  fontSize: '.72rem', color: colors.neutral[400], lineHeight: 1.5,
                  '& a': { color: colors.accent.main, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } },
                }}>
                  By continuing, you agree to our{' '}
                  <Link prefetch={false} href={dataConstants.NAVS.TERMS} rel="noopener" target="_blank">Terms of Use</Link>
                  {' '}and{' '}
                  <Link prefetch={false} href={dataConstants.NAVS.PRIVACY} rel="noopener" target="_blank">Privacy Policy</Link>
                </Typography>
              </Box>

              <Box sx={{ width: '100%', maxWidth: 360, mt: 1 }}>
                <Button
                  fullWidth
                  data-event-name={usePassword ? (isSignUp ? "signup-password" : "login-password") : "send-magic-link"}
                  data-event-category={isPopup ? 'popup' : 'auth'}
                  data-event-label="email"
                  disabled={!!user?.email || !values.email || (usePassword && !values.password)}
                  type="submit"
                  onClick={(e) => {
                    if (usePassword) {
                      handlePasswordAuth(e);
                    } else {
                      setLoginLoading(true);
                      send.uevent(e);
                      tryPasswordlessSignIn(e);
                    }
                  }}
                  endIcon={!usePassword && <ArrowForwardRoundedIcon sx={{ fontSize: '1rem !important' }} />}
                  sx={{
                    textTransform: 'none', fontSize: '.875rem', fontWeight: 600,
                    bgcolor: colors.accent.main, color: '#fff', py: 1.2,
                    borderRadius: radii.sm,
                    '&:hover': { bgcolor: colors.accent.dark, boxShadow: shadows.md },
                    '&.Mui-disabled': { bgcolor: colors.neutral[200], color: colors.neutral[400] },
                  }}
                  variant="contained"
                  disableElevation
                >
                  {usePassword ? (isSignUp ? "Create Account" : "Sign In") : "Send Secure Link"}
                </Button>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 1, gap: 1 }}>
                {usePassword && (
                  <Typography sx={{ fontSize: '.813rem', color: colors.neutral[500] }}>
                    {isSignUp ? "Already have an account? " : "Don't have an account? "}
                    <span
                      style={{ color: colors.accent.main, cursor: 'pointer', fontWeight: 600 }}
                      onClick={() => {
                        setIsSignUp(!isSignUp);
                        setValues({ ...values, password: '' });
                      }}
                    >
                      {isSignUp ? "Sign in" : "Sign up"}
                    </span>
                  </Typography>
                )}

                <Button
                  variant="text"
                  onClick={() => {
                    setUsePassword(!usePassword);
                    if (usePassword) {
                      setIsSignUp(false);
                      setValues({ ...values, password: '' });
                    }
                  }}
                  sx={{
                    textTransform: 'none', fontSize: '.813rem', fontWeight: 500,
                    color: colors.neutral[500],
                    '&:hover': { color: colors.accent.main, bgcolor: 'transparent', textDecoration: 'underline' },
                    p: 0, minWidth: 'auto'
                  }}
                >
                  {usePassword ? "Use a magic link instead" : "Sign in with password instead"}
                </Button>
              </Box>
            </Box>

            <Box sx={{ width: '100%', maxWidth: 360, my: 3, mx: 'auto' }}>
              <Divider sx={{ '&::before, &::after': { borderColor: colors.neutral[200] } }}>
                <Chip label="or" size="small" sx={{
                  fontSize: '.75rem', fontWeight: 500, color: colors.neutral[400],
                  bgcolor: colors.neutral[50], border: `1px solid ${colors.neutral[200]}`,
                  height: 24,
                }} />
              </Divider>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, width: '100%' }}>
              <SocialButton
                data-event-name="login-button" data-event-category={isPopup ? 'popup' : 'auth'} data-event-label="Google"
                disabled={!!user?.email}
                onClick={(e) => { loginWithGoogle(e); send.uevent(e); setLoginLoading(true); }}
                startIcon={<Google48 />}
              >
                Continue with Google
              </SocialButton>

              <SocialButton
                data-event-name="login-button" data-event-category={isPopup ? 'popup' : 'auth'} data-event-label="Twitter"
                disabled={!!user?.email}
                onClick={(e) => { loginWithTwitter(e); send.uevent(e); setLoginLoading(true); }}
                startIcon={<TwitterIcon sx={{ fontSize: '1.25rem !important', color: user?.email ? colors.neutral[400] : '#000' }} />}
              >
                Continue with X (Twitter)
              </SocialButton>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );

  return (
    <>
      <Grid container spacing={{ xs: 0, md: 0 }}>
        {tabValue === 0 && isPopup && (
          <Grid size={{ xs: 12, sm: 12, md: 5 }}
            sx={{
              bgcolor: colors.primary.main,
              display: { md: loginLoading && !error?.[router?.pathname] ? 'none' : 'flex', sm: 'none', xs: 'none' },
              flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              borderRadius: isPopup ? `${radii.lg}px 0 0 ${radii.lg}px` : 0,
            }}
          >
            <LogoWhite isPopup={isPopup} />
          </Grid>
        )}

        <Grid
          size={{
            xs: 12,
            sm: tabValue === 1 ? 11 : 12,
            md: tabValue === 1 ? 10.5 : (isPopup ? 7 : 12),
          }}
          sx={{
            bgcolor: tabValue === 0 && isPopup ? colors.neutral[0] : 'transparent',
            p: tabValue === 1 ? 0 : { xs: '16px', md: '24px' },
            mx: 'auto',
            position: 'relative',
          }}
        >
          {/* Tab 0: Login / Sign-up */}
          {tabValue === 0 && renderLoginPanel()}

          {/* Tab 1: Premium banner (popup mode) */}
          {tabValue === 1 && isPopup && (
            <PremiumBanner logout={logout} comp={comp} user={user} />
          )}

          {/* Navigate to pricing tab */}
          {user?.email && tabValue === 0 && !isPremium && !loginLoading && (
            <IconButton
              sx={{
                position: 'absolute', right: 16, top: 16,
                bgcolor: colors.neutral[100], zIndex: 2,
                '&:hover': { bgcolor: colors.neutral[200] },
              }}
              onClick={() => setTabValue(1)}
            >
              <NorthEastRoundedIcon fontSize="small" />
            </IconButton>
          )}
        </Grid>
      </Grid>

      <Dialog
        open={checkinboxpop && (values?.email || email) && !user?.email && !error?.[router?.pathname]}
        TransitionComponent={Transition}
        keepMounted
        aria-describedby="check-inbox-pop"
        fullWidth
        maxWidth="xs"
        sx={{
          '& .MuiDialog-backdrop': { backgroundColor: 'rgba(255,255,255,0.6)' },
          '& .MuiBackdrop-root': { backdropFilter: 'blur(12px)' },
          '& .MuiDialog-paper': {
            borderRadius: `${radii.xl}px`,
            border: `1px solid ${colors.neutral[200]}`,
            boxShadow: '0 20px 60px -12px rgba(0,0,0,0.15)',
          },
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', pt: 4 }}><MailIcon /></DialogTitle>
        <DialogContent sx={{ textAlign: 'center', pb: 1 }}>
          <Typography sx={{ fontWeight: 700, color: colors.neutral[900], fontSize: '1.25rem', letterSpacing: '-0.02em' }}>
            Check your email
          </Typography>
          <Typography sx={{ mt: 1.5, fontSize: '.938rem', color: colors.neutral[500], display: 'block', }}>
            We sent a sign-in link to
          </Typography>
          <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.5,
            mt: 0.5, px: 2, py: 0.5,
            bgcolor: colors.accent[50], borderRadius: radii.sm,
          }}>
            <Typography sx={{ color: colors.accent.dark, fontWeight: 600, fontSize: '.938rem' }}>
              {values?.email ?? email?.current}
            </Typography>
            <Tooltip title="Edit email" placement="right">
              <IconButton size="small" onClick={() => { setCheckinboxpop(false); }}>
                <BorderColorRoundedIcon sx={{ fontSize: '.85rem', color: colors.accent.main }} />
              </IconButton>
            </Tooltip>
          </Box>
          <Typography sx={{ mt: 2.5, fontSize: '.875rem', color: colors.neutral[500], display: 'block', }}>
            Click the link in the email to sign in
          </Typography>
          {values?.email?.toLowerCase().includes('gmail') && (
            <Box sx={{ mt: 3 }}>
              <Button
                endIcon={<NorthEastRoundedIcon sx={{ fontSize: '1rem !important' }} />}
                variant="outlined"
                target="_blank"
                rel="noopener noreferrer"
                href="https://mail.google.com/mail/u/0/#search/from%3A%40Grufity+in%3Aanywhere"
                sx={{
                  textTransform: 'none', borderRadius: radii.sm, fontWeight: 600,
                  borderColor: colors.accent[200], color: colors.accent.main,
                  '&:hover': { bgcolor: colors.accent[50], borderColor: colors.accent.main },
                }}
              >
                Open Gmail
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Typography variant="caption" sx={{ color: colors.neutral[400], fontSize: '.78rem' }}>
            Return to this tab after clicking the link.
          </Typography>
        </DialogActions>
      </Dialog>

      {/* Payment processing backdrop */}
      <Backdrop sx={{ color: '#fff', zIndex: 10 }} open={openBackdrop}>
        <Typography sx={{ textAlign: 'center' }}>Processing your payment..</Typography>
      </Backdrop>
    </>
  );
}
